import {
  isCanvasPosInitialized,
  MAX_ZOOM,
  MIN_ZOOM,
  useCanvasStore,
} from "@/store/zustand/canvasStore";
import { MutableRefObject, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  setRequestedContextPosition,
  setShowContextMenu,
} from "@/store/iframeSlice";
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  getPointOnCanvasFromInsideIframe,
} from "../utils";
import { useActiveCanvas } from "@/utils/utils";

export const useOnWheel = (
  canvasRef: MutableRefObject<any>,
  iframesRef: MutableRefObject<{ [storyboardId: string]: any }>
) => {
  const dispatch = useDispatch<any>();

  const setCanvasPosValue = useCanvasStore((store) => store.setCanvasPosValue);

  const activeCanvas = useActiveCanvas();
  const activeCanvasIdRef = useRef(activeCanvas?.id);

  const pendingPos = useRef<{ id: string; pos: any } | null>(null);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    activeCanvasIdRef.current = activeCanvas?.id;
  }, [activeCanvas?.id]);

  const flush = useCallback(() => {
    if (pendingPos.current) {
      setCanvasPosValue(pendingPos.current.id, pendingPos.current.pos);
      pendingPos.current = null;
    }
    rafId.current = null;
  }, []);

  const scheduleFlush = () => {
    if (rafId.current === null) {
      rafId.current = requestAnimationFrame(flush);
    }
  };

  const onWheel = useCallback((e: WheelEvent, storyboardId: string) => {
    if (!storyboardId) {
      e.preventDefault();
      e.stopPropagation();
    }

    dispatch(setShowContextMenu(false));
    dispatch(setRequestedContextPosition(null));

    if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
      return;
    }

    const canvasBoundingRect = canvasRef?.current?.getBoundingClientRect();

    const {
      x: _pointOnCanvasX,
      y: _pointOnCanvasY,
      mouseX,
      mouseY,
    } = getPointOnCanvasFromInsideIframe(
      e.x,
      e.y,
      canvasRef,
      iframesRef,
      storyboardId
    );

    // The logic for this is a bit outdated, so we need to adjust it to the overall canvas coordinates,
    // not assuming the canvas middle is 0, 0
    const pointOnCanvasX = _pointOnCanvasX + CANVAS_WIDTH / 2;
    const pointOnCanvasY = _pointOnCanvasY + CANVAS_HEIGHT / 2;

    const deltaY = e.deltaY;
    // @ts-ignore TS2339
    const wheelDelta = e.wheelDelta;
    const deltaX = e.deltaX;

    if (deltaY === 0 && deltaX === 0) return;

    let modeToUse = "";

    if (e.ctrlKey || e.metaKey) {
      modeToUse = "zoom";
    } else {
      modeToUse = "pan";
    }

    const activeCanvasId = activeCanvasIdRef.current;
    const canvasState = useCanvasStore.getState();
    const canvasPos =
      activeCanvasId &&
      (canvasState.canvasIdToPosRealtime[activeCanvasId] ||
        canvasState.canvasIdToPos[activeCanvasId]);

    if (!canvasPos || !isCanvasPosInitialized(canvasPos)) {
      return;
    }

    const prevZoomPerc = canvasPos.zoomPerc;

    if (modeToUse == "zoom") {
      // TODO: Improve zoom logic to make it feel better (allow slow zooming)
      const percentChange = wheelDelta / 240;
      let changeMultiplier = 1 + (e.ctrlKey ? 0.1 : 0.1) * percentChange;
      let newZoomPerc = prevZoomPerc * changeMultiplier;
      if (newZoomPerc > MAX_ZOOM) {
        newZoomPerc = MAX_ZOOM;
        changeMultiplier = newZoomPerc / prevZoomPerc;
      } else if (newZoomPerc < MIN_ZOOM) {
        newZoomPerc = MIN_ZOOM;
        changeMultiplier = newZoomPerc / prevZoomPerc;
      }

      // Also move the canvas so it appears you are zooming into the mouse position

      // Calculate the new transform values
      // This is needed because the transforms are scaled by the zoom percentages to maintain their values
      // as expected on the canvas grid
      const prevTransformXCalculated =
        canvasPos.transformX * prevZoomPerc * 0.45;
      const newTransformXCalculated = canvasPos.transformX * newZoomPerc * 0.45;
      const transformXDelta =
        newTransformXCalculated - prevTransformXCalculated;

      const prevTransformYCalculated =
        canvasPos.transformY * prevZoomPerc * 0.45;
      const newTransformYCalculated = canvasPos.transformY * newZoomPerc * 0.45;
      const transformYDelta =
        newTransformYCalculated - prevTransformYCalculated;

      // Post zoom the canvas left and top will move as well
      const newCanvasWidth = canvasBoundingRect.width * changeMultiplier;
      const newCanvasLeft =
        canvasBoundingRect.left +
        (canvasBoundingRect.width - newCanvasWidth) / 2 -
        transformXDelta;
      const newCanvasHeight = canvasBoundingRect.height * changeMultiplier;
      const newCanvasTop =
        canvasBoundingRect.top +
        (canvasBoundingRect.height - newCanvasHeight) / 2 -
        transformYDelta;

      // The formula is basically that the mouse should stay on the same point in the canvas after the new zoom
      const newPointOnCanvasX =
        // @ts-expect-error TS18048
        ((mouseX - newCanvasLeft) / newCanvasWidth) * CANVAS_WIDTH;
      const newPointOnCanvasY =
        // @ts-expect-error TS18048
        ((mouseY - newCanvasTop) / newCanvasHeight) * CANVAS_HEIGHT;

      // The point on the canvas should stay under the mouse, work backwards to calculate the transforms
      const xChange = pointOnCanvasX - newPointOnCanvasX;
      const yChange = pointOnCanvasY - newPointOnCanvasY;

      const newPos = {
        zoomPerc: newZoomPerc,
        transformX: canvasPos.transformX + xChange,
        transformY: canvasPos.transformY + yChange,
      };

      pendingPos.current = { id: activeCanvasId, pos: newPos };
      scheduleFlush();
    } else {
      pendingPos.current = {
        id: activeCanvasId,
        pos: {
          zoomPerc: canvasPos.zoomPerc,
          transformX: canvasPos.transformX + deltaX / canvasPos.zoomPerc,
          transformY: canvasPos.transformY + deltaY / canvasPos.zoomPerc,
        },
      };
      scheduleFlush();
    }

    return false;
  }, []);

  useEffect(
    () => () => {
      rafId.current && cancelAnimationFrame(rafId.current);
      pendingPos.current = null;
    },
    []
  );
  return onWheel;
};
